"use client"

import { Mail, MapPin, Phone, Send, MessageSquare, Clock, Globe } from "lucide-react"
import { motion } from "framer-motion"
import { Button } from "@/components/ui/button"

const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
}

const item = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] } }
}

export default function ContactPage() {
  return (
    <div className="bg-white min-h-screen">
      
      {/* Cinematic Header */}
      <section className="bg-slate-950 pt-32 pb-20 relative overflow-hidden">
        <div className="absolute inset-0 z-0 opacity-20">
          <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_50%_50%,rgba(180,140,54,0.15),transparent_70%)]" />
        </div>
        
        <div className="max-w-7xl mx-auto px-6 sm:px-8 relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="flex items-center justify-center gap-3 mb-6">
              <div className="w-10 h-px bg-[#b48c36]/50" />
              <p className="text-[#b48c36] font-bold text-[10px] uppercase tracking-[0.5em]">
                Direct Channel
              </p>
              <div className="w-10 h-px bg-[#b48c36]/50" />
            </div>
            <h1 className="text-4xl sm:text-6xl font-serif font-medium text-white mb-6 tracking-tight">
              Establish Your <span className=" font-light">Connection.</span>
            </h1>
            <p className="text-slate-400 max-w-2xl mx-auto text-lg font-light leading-relaxed px-4">
              Whether you're seeking guidance for TSPSC or APPSC, our expert faculty and support team are ready to assist your administrative journey.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-24 max-w-7xl mx-auto px-6 sm:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
          
          {/* Contact Details (Left) */}
          <motion.div 
            className="lg:col-span-5 space-y-12"
            variants={container}
            initial="hidden"
            animate="show"
          >
            <motion.div variants={item} className="space-y-4">
              <h2 className="text-2xl font-serif font-medium text-slate-950 flex items-center gap-3">
                 <span className="w-8 h-px bg-[#b48c36]" /> The Headquarters
              </h2>
              <div className="flex gap-5 p-6 bg-slate-50 border border-slate-100 group hover:bg-white hover:shadow-xl hover:shadow-slate-200/50 transition-all duration-500">
                <div className="w-12 h-12 bg-slate-950 text-[#b48c36] flex items-center justify-center shrink-0">
                  <MapPin size={20} />
                </div>
                <div>
                  <h3 className="text-[10px] font-black uppercase tracking-widest text-[#b48c36] mb-2">Location</h3>
                  <p className="text-slate-600 text-sm leading-relaxed font-medium font-serif">
                    Rohith's Academy,<br />
                    123 Educational Complex, Ashok Nagar<br />
                    Hyderabad, Telangana 500020
                  </p>
                </div>
              </div>
            </motion.div>

            <motion.div variants={item} className="space-y-4">
              <h2 className="text-2xl font-serif font-medium text-slate-950 flex items-center gap-3">
                 <span className="w-8 h-px bg-[#b48c36]" /> Direct Lines
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-4">
                <div className="flex gap-5 p-6 bg-slate-50 border border-slate-100 group hover:bg-white hover:shadow-xl hover:shadow-slate-200/50 transition-all duration-500">
                  <div className="w-12 h-12 bg-slate-950 text-[#b48c36] flex items-center justify-center shrink-0">
                    <Phone size={20} />
                  </div>
                  <div>
                    <h3 className="text-[10px] font-black uppercase tracking-widest text-[#b48c36] mb-2">Call Support</h3>
                    <p className="text-slate-950 text-sm font-bold font-serif">+91 98765 43210</p>
                    <p className="text-slate-400 text-[10px] uppercase font-bold tracking-wider mt-1">Mon-Sat • 9am-7pm</p>
                  </div>
                </div>

                <div className="flex gap-5 p-6 bg-slate-50 border border-slate-100 group hover:bg-white hover:shadow-xl hover:shadow-slate-200/50 transition-all duration-500">
                  <div className="w-12 h-12 bg-slate-950 text-[#b48c36] flex items-center justify-center shrink-0">
                    <Mail size={20} />
                  </div>
                  <div>
                    <h3 className="text-[10px] font-black uppercase tracking-widest text-[#b48c36] mb-2">Electronic Mail</h3>
                    <p className="text-slate-950 text-sm font-bold font-serif">support@rohithsacademy.com</p>
                    <p className="text-slate-400 text-[10px] uppercase font-bold tracking-wider mt-1">Typical Reply: 2 Hours</p>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div variants={item} className="p-8 border-l border-[#b48c36]/30 bg-slate-50 font-serif  text-slate-500 text-lg leading-relaxed">
              "Excellence is not an act, but a habit. We are here to ensure your preparation habits are optimized for the highest ranks."
            </motion.div>
          </motion.div>

          {/* Contact Form (Right) */}
          <motion.div 
            className="lg:col-span-7"
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="bg-white border border-slate-200 p-8 sm:p-12 shadow-[0_32px_64px_-16px_rgba(0,0,0,0.08)] relative">
              <div className="absolute top-0 right-0 w-24 h-24 bg-slate-50 border-b border-l border-slate-200 flex items-center justify-center">
                 <MessageSquare className="text-slate-200" size={32} />
              </div>
              
              <div className="mb-12">
                <h2 className="text-3xl font-serif font-medium text-slate-950 mb-4">Official Inquiry</h2>
                <p className="text-slate-500 text-sm font-light">Submit your details below and an academic counselor will reach out within one business cycle.</p>
              </div>

              <form className="space-y-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="space-y-2">
                    <label className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">First Name</label>
                    <input
                      type="text"
                      className="w-full bg-transparent border-b border-slate-200 py-3 text-slate-950 focus:border-[#b48c36] outline-none transition-all placeholder:text-slate-300 font-serif text-lg"
                      placeholder="e.g. Siddhartha"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Surname</label>
                    <input
                      type="text"
                      className="w-full bg-transparent border-b border-slate-200 py-3 text-slate-950 focus:border-[#b48c36] outline-none transition-all placeholder:text-slate-300 font-serif text-lg"
                      placeholder="e.g. Reddy"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="space-y-2">
                    <label className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Email Identity</label>
                    <input
                      type="email"
                      className="w-full bg-transparent border-b border-slate-200 py-3 text-slate-950 focus:border-[#b48c36] outline-none transition-all placeholder:text-slate-300 font-serif text-lg"
                      placeholder="scholar@example.com"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Subject of Inquiry</label>
                    <select className="w-full bg-transparent border-b border-slate-200 py-3 text-slate-950 focus:border-[#b48c36] outline-none transition-all font-serif text-lg appearance-none">
                      <option>Admission & Programs</option>
                      <option>Test Series Verification</option>
                      <option>Administrative Support</option>
                      <option>Partnership Inquiry</option>
                    </select>
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Message Ledger</label>
                  <textarea
                    rows={4}
                    className="w-full bg-transparent border-b border-slate-200 py-3 text-slate-950 focus:border-[#b48c36] outline-none transition-all placeholder:text-slate-300 font-serif text-lg resize-none"
                    placeholder="Describe your requirement in detail..."
                  ></textarea>
                </div>

                <button
                  type="submit"
                  className="w-full py-5 bg-slate-950 text-white font-bold text-[10px] uppercase tracking-[0.3em] hover:bg-[#b48c36] transition-all duration-500 shadow-2xl flex items-center justify-center gap-3"
                >
                  Dispatch Inquiry <Send size={14} />
                </button>
              </form>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Map Section */}
      <section className="h-[500px] w-full border-y border-slate-200 grayscale contrast-125 opacity-80 hover:grayscale-0 hover:opacity-100 transition-all duration-1000">
        <iframe
          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3806.5662825635907!2d78.47356!3d17.360589!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bcb91e47c47d2ef%3A0x1234567890!2sState%20PSC%20Academy%2C%20Hyderabad!5e0!3m2!1sen!2sin!4v1234567890"
          width="100%"
          height="100%"
          style={{ border: 0 }}
          allowFullScreen
          loading="lazy"
          className="w-full h-full"
        ></iframe>
      </section>
      
    </div>
  )
}
